import { notifyError } from "@/lib/notify-error";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getUserFacingErrorMessage } from "@/lib/user-facing-errors";
import {
  MilestoneEditor,
  type MilestoneEditorStatus,
} from "@/features/field-pilot/milestone-editor";

export interface FieldPilotSubTaskDialogTarget {
  mode: "create" | "edit";
  /** Create: the single parent tag for the selected items. */
  tagId?: Id<"fieldPilotTags"> | null;
  /** Create: human-readable parent tag label, shown in the dialog. */
  tagName?: string;
  /** Create: the measurement items being grouped. Omitted when grouping the whole remainder. */
  itemIds?: Id<"fieldPilotItems">[];
  /**
   * Create: group everything on the tag that is not yet in a sub-task, rather than an explicit
   * selection. Membership is resolved server-side, so no ids are carried here (CON-405).
   */
  groupUngrouped?: boolean;
  /** Create: how many ungrouped items will be grouped, for the dialog's description. */
  ungroupedItemCount?: number;
  /** Create: number of distinct parent tags spanned by the selection (>1 blocks creating). */
  distinctTagCount?: number;
  /** Edit: the existing sub-task being edited. */
  subTaskId?: Id<"fieldPilotSubTasks"> | null;
  /** Edit: the existing sub-task's current name. */
  subTaskName?: string;
  /** This sub-task was created only to open milestones and should be removed if cancelled. */
  createdForMilestones?: boolean;
  /** The sole item in a sub-task created only to open milestones. */
  createdForMilestonesItemId?: Id<"fieldPilotItems">;
}

/** Header copy for the dialog's three jobs: set milestones, edit, or group into a sub-task. */
function describeDialog(target: FieldPilotSubTaskDialogTarget): {
  title: string;
  description: string;
} {
  if (target.createdForMilestones) {
    return {
      title: "Set milestones",
      description:
        "A new item schedule was created for this measurement. Add milestone due dates, or cancel to remove it.",
    };
  }
  if (target.mode === "edit") {
    return {
      title: "Edit sub-task",
      description: "Rename this sub-task or manage its milestone due dates.",
    };
  }
  return {
    title: "Group into sub-task",
    description: target.groupUngrouped
      ? "Group everything on this task that is not yet in a sub-task, then set milestone due dates."
      : "Group the selected measurements under a named sub-task, then set milestone due dates.",
  };
}

/** One line naming what the create phase is about to group. */
function describeCreateScope(target: FieldPilotSubTaskDialogTarget): string {
  const count = target.groupUngrouped
    ? target.ungroupedItemCount ?? 0
    : target.itemIds?.length ?? 0;
  const plural = count === 1 ? "" : "s";
  if (target.groupUngrouped) {
    const scope = target.tagName ? ` on ${target.tagName}` : "";
    return `${count} ungrouped item${plural}${scope} will be grouped.`;
  }
  const scope = target.tagName ? ` from ${target.tagName}` : "";
  return `${count} item${plural}${scope} selected.`;
}

interface FieldPilotSubTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: Id<"fieldPilotJobs">;
  statuses: MilestoneEditorStatus[];
  target: FieldPilotSubTaskDialogTarget;
  /** Opens the parent tag's schedule after discarding a newly created item schedule. */
  onEditParentSchedule?: () => void;
}

export function FieldPilotSubTaskDialog({
  open,
  onOpenChange,
  jobId,
  statuses,
  target,
  onEditParentSchedule,
}: FieldPilotSubTaskDialogProps) {
  const createFromItems = useMutation(api.fieldPilot.subTasks.createFromItems);
  const createFromUngrouped = useMutation(api.fieldPilot.ungroupedItems.createFromUngrouped);
  const discard = useMutation(api.fieldPilot.subTasks.discard);
  const rename = useMutation(api.fieldPilot.subTasks.rename);
  const upsertMilestone = useMutation(api.fieldPilot.subTasks.upsertMilestone);
  const removeMilestone = useMutation(api.fieldPilot.subTasks.removeMilestone);
  const setStart = useMutation(api.fieldPilot.subTasks.setStart);

  const [name, setName] = useState("");
  const [createdSubTaskId, setCreatedSubTaskId] = useState<Id<"fieldPilotSubTasks"> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasScheduleEdits, setHasScheduleEdits] = useState(false);
  const [pendingScheduleWrites, setPendingScheduleWrites] = useState(0);

  // Re-seed local state each time the dialog opens for a (possibly new) target.
  useEffect(() => {
    if (!open) return;
    setName(target.mode === "edit" ? target.subTaskName ?? "" : "");
    setCreatedSubTaskId(null);
    setIsSubmitting(false);
    setHasScheduleEdits(false);
    setPendingScheduleWrites(0);
  }, [open, target.mode, target.subTaskName, target.subTaskId]);

  const activeSubTaskId =
    target.mode === "edit" ? target.subTaskId ?? null : createdSubTaskId;

  const { data: subTasks = [] } = useQuery(
    convexQuery(api.fieldPilot.subTasks.listByJob, open ? { jobId } : "skip")
  );

  const activeSubTask = useMemo(
    () =>
      activeSubTaskId
        ? subTasks.find((entry) => String(entry._id) === String(activeSubTaskId)) ?? null
        : null,
    [subTasks, activeSubTaskId]
  );

  const dueDateByStatusId = useMemo(() => {
    const map = new Map<string, string>();
    for (const milestone of activeSubTask?.milestones ?? []) {
      map.set(String(milestone.statusId), milestone.dueDateKey);
    }
    return map;
  }, [activeSubTask]);

  // `startDateKey` on the sub-task is the explicit start override; empty when it inherits the task.
  const startDateKey = activeSubTask?.startDateKey ?? "";

  const copy = describeDialog(target);
  const spansMultipleTags = target.mode === "create" && (target.distinctTagCount ?? 0) > 1;
  const trimmedName = name.trim();

  const handleCreate = async () => {
    if (isSubmitting || !target.tagId) return;
    if (!target.groupUngrouped && !target.itemIds?.length) return;
    if (!trimmedName) {
      toast.error("Sub-task name is required");
      return;
    }
    setIsSubmitting(true);
    try {
      const subTaskId = target.groupUngrouped
        ? (await createFromUngrouped({ tagId: target.tagId, name: trimmedName })).subTaskId
        : await createFromItems({
            tagId: target.tagId,
            name: trimmedName,
            itemIds: target.itemIds ?? [],
          });
      setCreatedSubTaskId(subTaskId);
      toast.success("Sub-task created. Add milestone dates below.");
    } catch (error) {
      notifyError(error, getUserFacingErrorMessage(error, "Failed to create sub-task"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRename = async () => {
    if (!activeSubTaskId) return;
    const next = trimmedName;
    if (!next || next === (target.subTaskName ?? "")) return;
    try {
      await rename({ subTaskId: activeSubTaskId, name: next });
    } catch (error) {
      notifyError(error, getUserFacingErrorMessage(error, "Failed to rename sub-task"));
    }
  };

  const runScheduleWrite = (write: () => Promise<unknown>, errorMessage: string) => {
    setPendingScheduleWrites((count) => count + 1);
    void write()
      .then(() => {
        setHasScheduleEdits(true);
      })
      .catch((error) => {
        notifyError(error, getUserFacingErrorMessage(error, errorMessage));
      })
      .finally(() => {
        setPendingScheduleWrites((count) => Math.max(0, count - 1));
      });
  };

  const handleSetMilestone = (statusId: Id<"fieldPilotStatuses">, dueDateKey: string) => {
    if (!activeSubTaskId) return;
    runScheduleWrite(
      () => upsertMilestone({ subTaskId: activeSubTaskId, statusId, dueDateKey }),
      "Failed to set milestone"
    );
  };

  const handleRemoveMilestone = (statusId: Id<"fieldPilotStatuses">) => {
    if (!activeSubTaskId) return;
    removeMilestone({ subTaskId: activeSubTaskId, statusId }).catch((error) =>
      notifyError(error, getUserFacingErrorMessage(error, "Failed to remove milestone"))
    );
  };

  const handleSetStart = (nextStartDateKey: string | null) => {
    if (!activeSubTaskId) return;
    runScheduleWrite(
      () => setStart({ subTaskId: activeSubTaskId, startDateKey: nextStartDateKey }),
      "Failed to set start date"
    );
  };

  const isCreatePhase = target.mode === "create" && !createdSubTaskId;
  const createdMilestoneItemId = target.createdForMilestonesItemId;
  const isBusy = isSubmitting || pendingScheduleWrites > 0;
  const shouldDiscardOnClose =
    target.createdForMilestones &&
    !hasScheduleEdits &&
    !!activeSubTaskId &&
    !!createdMilestoneItemId;

  const closeDialog = async (editParentSchedule = false) => {
    if (pendingScheduleWrites > 0) return;
    // "Edit parent instead" still reverses the auto-created sub-task even after date edits.
    const shouldDiscard =
      !!activeSubTaskId &&
      !!createdMilestoneItemId &&
      target.createdForMilestones &&
      (editParentSchedule || !hasScheduleEdits);
    if (!shouldDiscard || !activeSubTaskId || !createdMilestoneItemId) {
      onOpenChange(false);
      if (editParentSchedule) onEditParentSchedule?.();
      return;
    }
    setIsSubmitting(true);
    try {
      await discard({
        subTaskId: activeSubTaskId,
        itemId: createdMilestoneItemId,
      });
      onOpenChange(false);
      if (editParentSchedule) onEditParentSchedule?.();
    } catch (error) {
      notifyError(error, getUserFacingErrorMessage(error, "Failed to remove new sub-task"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (isBusy) return;
        if (nextOpen) onOpenChange(true);
        else void closeDialog();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {target.mode === "create" ? (
            <p className="text-xs text-muted-foreground">{describeCreateScope(target)}</p>
          ) : null}

          {spansMultipleTags ? (
            <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertTriangle className="size-4 shrink-0" />
              <span>
                Your selection spans multiple tasks. A sub-task must belong to a single task —
                select measurements from one task only.
              </span>
            </div>
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor="sub-task-name">Sub-task name</Label>
            <Input
              id="sub-task-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              onBlur={() => {
                if (!isCreatePhase) void handleRename();
              }}
              placeholder="e.g. North wing"
              autoFocus
              disabled={isBusy || spansMultipleTags}
              onKeyDown={(event) => {
                if (event.key === "Enter" && isCreatePhase) {
                  event.preventDefault();
                  void handleCreate();
                }
              }}
            />
          </div>

          {activeSubTaskId ? (
            <MilestoneEditor
              statuses={statuses}
              dueDateByStatusId={dueDateByStatusId}
              startDateKey={startDateKey}
              onSetStart={handleSetStart}
              onSetMilestone={handleSetMilestone}
              onRemoveMilestone={handleRemoveMilestone}
            />
          ) : null}
        </div>

        <DialogFooter className="sm:justify-between">
          {target.createdForMilestones ? (
            <Button
              type="button"
              variant="link"
              className="px-0"
              onClick={() => void closeDialog(true)}
              disabled={isBusy}
            >
              Edit parent task schedule instead
            </Button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => void closeDialog()}
              disabled={isBusy}
            >
              {isCreatePhase || shouldDiscardOnClose ? "Cancel" : "Done"}
            </Button>
            {isCreatePhase ? (
              <Button
                type="button"
                onClick={() => void handleCreate()}
                disabled={isBusy || spansMultipleTags || !trimmedName}
              >
                {isSubmitting ? "Creating..." : "Create sub-task"}
              </Button>
            ) : null}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
